import React, {
  createContext, useContext, useState, useCallback, ReactNode,
  useEffect, useMemo, useRef,
} from 'react';
import { toast } from 'sonner';
import { CartItem, Product } from '../types';
import { budgetService } from '../services/budgetService';
import type { CartBudgetBreakdown } from '../services/budgetService';
import { cartService } from '../services/cartService';
import { productService } from '../services/productService';
import { getCartItemUnitPrice } from '../utils/cartPrice';
import { useAuth } from './AuthContext';

interface CartContextValue {
  items: CartItem[];
  /** Aggiunge al carrello, mai oltre la disponibilità. Rende i pezzi aggiunti davvero. */
  addItem: (product: Product, qty?: number) => number;
  removeItem: (sku: string) => void;
  updateQty: (sku: string, qty: number) => void;
  clearCart: () => void;
  totalItems: number;
  subtotale: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isCartLoading: boolean;
  /** Anteprima sconto Budget Premium (Fase 2). `null` se non caricato/non applicabile. */
  budgetPreview: CartBudgetBreakdown | null;
  budgetPreviewLoading: boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

const BUDGET_PREVIEW_DEBOUNCE_MS = 350;
const CART_SYNC_DEBOUNCE_MS = 400;

/** Pezzi ordinabili di un articolo: ``stock`` del prodotto è già la
 * disponibilità (esistenza − impegnato, API-82/API-85). ``null`` = giacenza
 * non tracciata: non si blocca, come ``available_qty`` lato backend. */
export function stockCap(stock: number | null | undefined): number | null {
  return typeof stock === 'number' ? Math.max(0, Math.floor(stock)) : null;
}

/** Ricarica giacenza e vendibilità correnti delle righe: quelle salvate nel
 * carrello sono una copia del momento dell'aggiunta, e invecchiano. Le righe
 * oltre la disponibilità scendono, quelle esaurite escono. */
export async function refreshCartStock(items: CartItem[]): Promise<{
  items: CartItem[]; reduced: string[]; removed: string[];
}> {
  const skus = Array.from(new Set(items.map(i => i.sku)));
  if (skus.length === 0) return { items, reduced: [], removed: [] };
  const res = await productService.list({ skus: skus.join(','), limit: skus.length });
  const bySku = new Map(res.items.map(p => [p.sku, p]));
  const reduced: string[] = [];
  const removed: string[] = [];
  const next: CartItem[] = [];
  for (const it of items) {
    const p = bySku.get(it.sku);
    // Articolo non più in catalogo: lo lascia alla validazione del checkout.
    if (!p) { next.push(it); continue; }
    const line: CartItem = { ...it, stock: p.stock, webSellable: p.webSellable, rimossoDalGestionale: p.rimossoDalGestionale };
    const cap = stockCap(p.stock);
    if (cap !== null && line.qty > cap) {
      if (cap === 0) { removed.push(it.sku); continue; }
      line.qty = cap;
      reduced.push(it.sku);
    }
    next.push(line);
  }
  return { items: next, reduced, removed };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user, canAddToCart } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [budgetPreview, setBudgetPreview] = useState<CartBudgetBreakdown | null>(null);
  const [budgetPreviewLoading, setBudgetPreviewLoading] = useState(false);

  const budgetDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether items were loaded from server (avoid sync-back on first load)
  const loadedRef = useRef(false);
  // Track userId to detect user/impersonation changes
  const userIdRef = useRef<string | null>(null);
  // Copia sincrona di ``items``: ``addItem`` deve sapere subito quanti pezzi
  // ha aggiunto, anche quando è chiamato più volte nello stesso tick (riordino).
  const itemsRef = useRef<CartItem[]>([]);
  const commitItems = useCallback((next: CartItem[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  // ─── Carica carrello da MongoDB al login / cambio utente (o impersonazione) ───
  useEffect(() => {
    const newUserId = user?.userId ?? null;

    if (!newUserId) {
      // Logout → svuota stato locale
      commitItems([]);
      loadedRef.current = false;
      userIdRef.current = null;
      return;
    }

    if (newUserId === userIdRef.current) return; // stessa sessione, nessun reload

    userIdRef.current = newUserId;
    loadedRef.current = false;
    setIsCartLoading(true);

    cartService.getCart()
      .then(async res => {
        const saved = (res.items ?? []) as CartItem[];
        let loaded = saved;
        let reduced: string[] = [];
        let removed: string[] = [];
        try {
          ({ items: loaded, reduced, removed } = await refreshCartStock(saved));
        } catch {
          // Catalogo irraggiungibile: restano le righe salvate, il checkout
          // rivaluta comunque la disponibilità alla conferma.
        }
        // Un cambio utente durante il fetch rende obsoleto questo carrello.
        if (userIdRef.current !== newUserId) return;
        if (removed.length) {
          toast.warning(`Esauriti, rimossi dal carrello: ${removed.join(', ')}`);
        }
        if (reduced.length) {
          toast.warning(`Quantità ridotta alla disponibilità: ${reduced.join(', ')}`);
        }
        // ``loadedRef`` prima del commit: se il refresh ha cambiato le righe,
        // il sync le riscrive su Mongo; se no è un PUT identico, innocuo.
        loadedRef.current = true;
        commitItems(loaded);
      })
      .catch(() => {
        // Errore di rete → carrello vuoto, tentiamo di nuovo al prossimo cambio
        loadedRef.current = true;
        commitItems([]);
      })
      .finally(() => setIsCartLoading(false));
  }, [user?.userId, commitItems]);

  // ─── Sincronizza su MongoDB ogni volta che items cambia (debounced) ───
  useEffect(() => {
    if (!loadedRef.current) return; // non sincronizzare durante il caricamento iniziale
    if (!user?.userId) return;

    if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
    syncDebounceRef.current = setTimeout(() => {
      cartService.syncCart(items).catch(() => {
        // Silently fail — il carrello locale rimane valido
      });
    }, CART_SYNC_DEBOUNCE_MS);

    return () => { if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ─── Subtotale ───
  const subtotale = useMemo(
    () => items.reduce((sum, i) => sum + getCartItemUnitPrice(i) * i.qty, 0),
    [items],
  );

  // ─── Budget preview (debounced) ───
  useEffect(() => {
    if (budgetDebounceRef.current) clearTimeout(budgetDebounceRef.current);
    if (subtotale <= 0) {
      setBudgetPreview(null);
      return;
    }
    budgetDebounceRef.current = setTimeout(async () => {
      setBudgetPreviewLoading(true);
      try {
        const data = await budgetService.previewCart(subtotale);
        setBudgetPreview(data);
      } catch {
        setBudgetPreview(null);
      } finally {
        setBudgetPreviewLoading(false);
      }
    }, BUDGET_PREVIEW_DEBOUNCE_MS);
    return () => { if (budgetDebounceRef.current) clearTimeout(budgetDebounceRef.current); };
  }, [subtotale]);

  // ─── Mutatori ───
  // Il blocco del sotto-utente `vedi-sito` sta qui e non sui pulsanti: è
  // l'unico punto da cui un articolo entra nel carrello, e i punti di
  // chiamata sono una decina (PDP, ricerca, preferiti, storico, confronto,
  // riordino…). Il server rifiuta comunque il PUT, ma senza questa guardia
  // l'articolo comparirebbe nel drawer per poi sparire al reload.
  const addItem = useCallback((product: Product, qty: number = 1): number => {
    if (!canAddToCart) {
      toast.error('Il tuo profilo è di sola visualizzazione: non puoi aggiungere prodotti al carrello.');
      return 0;
    }
    if (product.rimossoDalGestionale) {
      toast.error('Articolo non più disponibile.');
      return 0;
    }
    // Non vendibile online su NTS (``ar_webvend`` N): non disponibile.
    if (product.webSellable === false) {
      toast.error('Articolo non disponibile per l’acquisto online.');
      return 0;
    }
    // Mai oltre la disponibilità, contando quanto è già nel carrello: ogni
    // punto di chiamata passa da qui, e non tutti controllano la giacenza.
    const prev = itemsRef.current;
    const existing = prev.find(i => i.sku === product.sku);
    const inCart = existing?.qty ?? 0;
    const cap = stockCap(product.stock);
    const added = cap === null ? qty : Math.max(0, Math.min(qty, cap - inCart));
    if (added <= 0) {
      toast.error(cap === 0 || inCart === 0
        ? `${product.sku}: articolo esaurito, non aggiunto al carrello.`
        : `${product.sku}: hai già nel carrello tutti i ${cap} pz disponibili.`);
      return 0;
    }
    if (added < qty) {
      toast.warning(`${product.sku}: aggiunti solo ${added} pz, ${cap} disponibili in tutto.`);
    }
    const next = existing
      ? prev.map(i => i.sku === product.sku
          ? { ...i, qty: i.qty + added, stock: product.stock ?? i.stock }
          : i)
      : [...prev, { ...product, qty: added }];
    commitItems(next);
    return added;
  }, [canAddToCart, commitItems]);

  const removeItem = useCallback((sku: string) => {
    commitItems(itemsRef.current.filter(i => i.sku !== sku));
  }, [commitItems]);

  const updateQty = useCallback((sku: string, qty: number) => {
    const prev = itemsRef.current;
    const line = prev.find(i => i.sku === sku);
    if (!line) return;
    const cap = stockCap(line.stock);
    const q = cap === null ? qty : Math.min(qty, cap);
    if (cap !== null && qty > cap && cap > 0) {
      toast.warning(`${sku}: solo ${cap} pz disponibili.`);
    }
    commitItems(q <= 0
      ? prev.filter(i => i.sku !== sku)
      : prev.map(i => i.sku === sku ? { ...i, qty: q } : i));
  }, [commitItems]);

  const clearCart = useCallback(() => {
    commitItems([]);
    // Sync immediato per lo svuotamento
    if (user?.userId) {
      cartService.syncCart([]).catch(() => {});
    }
  }, [user?.userId, commitItems]);

  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQty, clearCart,
      totalItems, subtotale,
      isOpen, setIsOpen,
      isCartLoading,
      budgetPreview, budgetPreviewLoading,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
